/// SAR search patterns — IAMSAR Volume III patterns used by the
/// Coastguard, RNLI + ships rendering assistance. Each pattern has
/// a geometry the helmsman steers, scaled to the search area + a
/// "track spacing" that depends on the search object (a small boat
/// is visible at less range than a yacht).
///
/// Two domains:
///
///   1. Pattern definitions — the geometry + when each is used.
///   2. A waypoint generator — computes the (x, y) corners of an
///      expanding-square or sector-search pattern around a given
///      datum, in nautical-mile coordinates relative to the datum.
///      Used by the UI figure to draw a representative pattern.
///
/// Pure data + plain JavaScript geometry; no DOM, no charts.

export type SearchPatternId =
  | "expanding-square"
  | "sector-search"
  | "parallel-track"
  | "creeping-line";

export interface SearchPattern {
  id: SearchPatternId;
  /// Display name.
  name: string;
  /// IAMSAR / ICAO short code.
  code: string;
  /// One-line gist.
  summary: string;
  /// When this pattern is the right choice.
  whenToUse: string;
  /// Geometry described in plain English — what the helmsman steers.
  geometry: string;
  /// Search-object size band this pattern suits.
  suitedFor: string;
  /// Pitfalls + execution notes.
  pitfalls: readonly string[];
}

export const SEARCH_PATTERNS: readonly SearchPattern[] = Object.freeze([
  {
    id: "expanding-square",
    name: "Expanding-square search",
    code: "SS",
    summary:
      "Square spiral outward from a datum point — covers a small area completely from a known starting position.",
    whenToUse:
      "When the search object's last-known position is well-fixed (e.g. exact MOB position + minimal drift). Datum at the centre of the search; expands outward in squares.",
    geometry:
      "From the datum, steer 1 leg length on heading 000°, then 1 leg on 090°, then 2 legs on 180°, then 2 legs on 270°, then 3 legs on 000° again, then 3 legs on 090°… Each pair of legs grows by one leg length. Track spacing = leg length × √2 cos(45°) so the pattern doesn't leave gaps.",
    suitedFor:
      "Small targets close to a known position: an MOB casualty with a PLB beacon, a wreckage debris cluster, a missing dinghy whose course was logged just before loss.",
    pitfalls: [
      "Wind drift during execution offsets the whole pattern downwind. Re-anchor the datum to the casualty's drift rate every 30 min if practicable.",
      "Helmsman tired — easy to lose count of legs. Quartermaster keeps a written tally on a slate.",
      "Track spacing too wide for the visibility — gaps between sweeps let the target slip through. Halve the spacing if vis < 2 nm.",
    ],
  },
  {
    id: "sector-search",
    name: "Sector search",
    code: "VS",
    summary:
      "Three triangular sectors with a common vertex at the datum, each rotated 120° from the last — covers a small area from three directions.",
    whenToUse:
      "When the datum is well-known but the target is small + difficult to spot (a swimmer's head, a barrel). Three-direction coverage maximises detection from different sun + sea-state angles.",
    geometry:
      "From the datum, steer outward to a corner, turn 120° starboard, steer back across (over the datum) to the opposite corner, turn 120° starboard, steer back to the original side, turn 120° starboard… After three triangles, rotate the whole pattern 30° and repeat.",
    suitedFor:
      "Person in the water; small debris with the casualty's clothing — items where viewing angle matters because of low contrast or partial submersion.",
    pitfalls: [
      "Helm precision must be high — each triangle's apex returns to the datum, so heading error accumulates.",
      "Heavy seas — can lose the datum while passing through the corner; mark with a smoke flare or a buoy.",
      "Time-consuming for the area covered. Use only on small areas (< 2 nm radius).",
    ],
  },
  {
    id: "parallel-track",
    name: "Parallel-track search",
    code: "PS",
    summary:
      "Series of parallel legs (like mowing a lawn) within a rectangular search box — covers a large area uniformly.",
    whenToUse:
      "When the datum is uncertain or the search area is large (a missing yacht, an EPIRB hit with a wide LKP uncertainty). Multiple search units can share legs of the same pattern in coordinated SAR.",
    geometry:
      "Establish the search box (rectangle) — its long side aligned with the wind / sea / probable drift direction. Steer along one long side, turn 90° at the end + steer one track-spacing across, turn 90° + steer the next long-side leg in the opposite direction…",
    suitedFor:
      "Yacht-sized targets at long range — visible at 5+ nm in good visibility, so track spacing of 5 nm works. Large drifting debris.",
    pitfalls: [
      "Track spacing too wide — gaps. Spacing should be ~70-80% of the maximum detection range under the conditions.",
      "Drift across the box not accounted for — pattern walks downwind; re-orient the box after every 4 legs.",
      "Several search units running parallel-track must coordinate via VHF to avoid overlap + collision.",
    ],
  },
  {
    id: "creeping-line",
    name: "Creeping-line ahead",
    code: "CS",
    summary:
      "Like parallel-track but the long legs are PERPENDICULAR to the probable drift direction. Each pass moves the pattern forward (with the drift).",
    whenToUse:
      "When the target is known to be drifting AND the drift direction is well-established (e.g. man in a liferaft with known leeway).",
    geometry:
      "Long legs perpendicular to drift; short turns of one track-spacing parallel to drift. The whole pattern advances along the drift direction with each pair of long legs.",
    suitedFor:
      "Drifting targets — liferafts, slick of debris, oil pollution. The pattern keeps up with the target's downwind progress.",
    pitfalls: [
      "Drift rate underestimated — pattern lags behind the target. Always overestimate slightly.",
      "Long legs need long sustained-course steering. Helm rotation important.",
    ],
  },
]);

export const SEARCH_PATTERN_IDS: readonly SearchPatternId[] = SEARCH_PATTERNS.map(
  (p) => p.id,
);

/// ───────────────────────────────────────────────────────────────────
/// Pattern waypoint generator — produces a sequence of (x, y) points
/// in nautical-mile coordinates relative to the datum (x = east,
/// y = north). Used by the apps/web figure to draw the patterns to
/// scale.
/// ───────────────────────────────────────────────────────────────────

export interface SearchWaypoint {
  /// Nautical miles east of the datum.
  x: number;
  /// Nautical miles north of the datum.
  y: number;
}

/// Generate an expanding-square pattern from the datum (0, 0).
/// `legSpacingNm` is the distance between concentric squares — set
/// to roughly the detection range under the conditions.
/// `legs` is the number of pattern legs to compute.
export function expandingSquare(
  legSpacingNm: number,
  legs: number,
): readonly SearchWaypoint[] {
  const points: SearchWaypoint[] = [{ x: 0, y: 0 }];
  // Heading sequence: N, E, S, W, N, E, S, W, ...
  // Leg lengths: 1, 1, 2, 2, 3, 3, 4, 4, ...
  const headings = [
    { dx: 0, dy: 1 }, // N
    { dx: 1, dy: 0 }, // E
    { dx: 0, dy: -1 }, // S
    { dx: -1, dy: 0 }, // W
  ];
  let x = 0;
  let y = 0;
  for (let leg = 0; leg < legs; leg++) {
    const headingIdx = leg % 4;
    const legLengthMultiplier = Math.floor(leg / 2) + 1;
    const h = headings[headingIdx]!;
    x += h.dx * legSpacingNm * legLengthMultiplier;
    y += h.dy * legSpacingNm * legLengthMultiplier;
    points.push({ x, y });
  }
  return points;
}

/// Generate a sector-search pattern — three triangles, each 120°
/// apart, with a common vertex at the datum (0, 0).
/// `radiusNm` is the leg length from the datum out to the far
/// corner.
export function sectorSearch(radiusNm: number): readonly SearchWaypoint[] {
  const points: SearchWaypoint[] = [{ x: 0, y: 0 }];
  // First triangle: datum → 030° outward → 150° back across → 270° to datum.
  // Each subsequent triangle rotates 120°. After 3 triangles the
  // pattern is rotated 30° + repeated, but we stop at one cycle for
  // the visualisation.
  for (let triangle = 0; triangle < 3; triangle++) {
    const baseBearing = 30 + triangle * 120;
    const tri = [
      bearingToXy(baseBearing, radiusNm),
      bearingToXy(baseBearing + 120, radiusNm),
      { x: 0, y: 0 },
    ];
    for (const p of tri) points.push(p);
  }
  return points;
}

/// Generate a parallel-track pattern — rectangular search box with
/// `widthNm` wide (east-west) and `lengthNm` long (north-south),
/// `spacingNm` between tracks. Pattern starts at the SW corner.
export function parallelTrack(
  widthNm: number,
  lengthNm: number,
  spacingNm: number,
): readonly SearchWaypoint[] {
  const points: SearchWaypoint[] = [];
  let y = 0;
  let direction: 1 | -1 = 1; // east first
  while (y <= widthNm) {
    if (direction === 1) {
      points.push({ x: 0, y });
      points.push({ x: lengthNm, y });
    } else {
      points.push({ x: lengthNm, y });
      points.push({ x: 0, y });
    }
    y += spacingNm;
    direction = direction === 1 ? -1 : 1;
  }
  return points;
}

/// Convert a compass bearing + distance to a Cartesian (x = east,
/// y = north) offset in nautical miles. Used internally by the
/// sector-search generator.
function bearingToXy(bearingDeg: number, nm: number): SearchWaypoint {
  const rad = (bearingDeg * Math.PI) / 180;
  return {
    x: nm * Math.sin(rad),
    y: nm * Math.cos(rad),
  };
}
